import merge from "lodash/merge";
import { messagingTemplateContentStringRegex } from "@constants/messaging-template-constants";
import { MESSAGING_TEMPLATE_DCUI_EMAIL_DEFAULT, MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME, MESSAGING_TEMPLATE_DCUI_REQUIRED_VARIABLE_NAME, MESSAGING_TEMPLATE_PIN_EMAIL_DEFAULT, MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME, MESSAGING_TEMPLATE_PIN_REQUIRED_VARIABLE_NAME, REQUIRED_MESSAGING_TEMPLATE_VARIABLES } from "@constants/messaging-template-default";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { FormattedMessagingTemplate, MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { getLanguageFileFieldNameForTemplateType, getTemplateFileFieldNameForTemplateType } from "@utils/messaging-template/messaging-template-util";
import { convertDotSeparatedStringToJSONObject, convertJSONObjectToDotSeparatedString } from "@utils/string-util";

export const getMessagingTemplateEmailSubjectData = (
    type: MessagingTemplateTypes,
    languageFile: { [key: string]: any }
) => {
    let subjectVariableValue = "";
    let shouldHaveSubjectVariable = false;
    let subjectVariableName = null;
    if (type === MessagingTemplateTypes.DCUI_EMAIL) {
        subjectVariableValue = languageFile?.dcuiEmail?.subject || "";
        shouldHaveSubjectVariable = true;
        subjectVariableName = MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME;
    } else if (type === MessagingTemplateTypes.PIN_EMAIL) {
        subjectVariableValue = languageFile?.emailpin?.subject || "";
        shouldHaveSubjectVariable = true;
        subjectVariableName = MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME
    }
    return {
        shouldHaveSubjectVariable,
        subjectVariableValue,
        subjectVariableName
    };
};

export const getVariablesFromMessagingTemplate = (template: string): { name: string; value: string; }[] => {
    const matches = template.match(messagingTemplateContentStringRegex);

    const variables: Array<{ name: string; value: string; }> = [];
    if (matches) {
        matches.forEach(match => {
            //@ts-ignore
            const splitVar = match.split(/!(.*)/s);
            if (splitVar && splitVar.length > 1) {
                const variableName = splitVar[0].substring(3, splitVar[0].length - 1);
                const variableValue = splitVar[1].substring(1, splitVar[1].length - 2);

                variables.push({
                    name: variableName,
                    value: variableValue
                });
            }
        });
    }

    return variables;
};

export const getMessagingTemplateVariables = (languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any }>>, template: string, selectedLanguage: LocaleTypes) => {
    const variables = getVariablesFromMessagingTemplate(template);

    let flattenedLanguageFile: { [key: string]: string } = {};
    if (languageFiles[selectedLanguage]) {
        flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[selectedLanguage]);
    }

    const finalVariables: Array<{ name: string; value: string; }> = [];

    variables.forEach(variable => {
        const { name, value } = variable;
        const isPropertyInLanguageFile = Object.keys(flattenedLanguageFile).includes(name);
        let updatedValue;
        if (!REQUIRED_MESSAGING_TEMPLATE_VARIABLES.includes(name)) {
            if (!isPropertyInLanguageFile && value) {// the variable doesn't exist in language files and we have a default value
                updatedValue = value;
            } else if (isPropertyInLanguageFile) {// the variable already exist in language files empty or not 
                updatedValue = flattenedLanguageFile[name];
            } else {
                updatedValue = value || "";
            }

            finalVariables.push({ name, value: updatedValue });
        }
    });

    return finalVariables;
}

export const getMessagingTemplates = (
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>,
    mergedEmailTemplateFields: EmailTemplateFields,
    messagingTemplateCache: MessagingTemplateCache
) => {
    return availableMessagingTemplateTypes.reduce((newSelectedMessagingTemplates, templateType) => {
        const templateFileFieldName = getTemplateFileFieldNameForTemplateType(templateType);
        const languageFileFieldName = getLanguageFileFieldNameForTemplateType(templateType);

        //if the property is set up to use a template for this template type
        if (
            mergedEmailTemplateFields[templateFileFieldName] &&
            mergedEmailTemplateFields[languageFileFieldName]
        ) {
            //check the cache and store the template in the selected list if found
            const uuid = mergedEmailTemplateFields[templateFileFieldName].split(".")[0];
            if (messagingTemplateCache[uuid]) {
                newSelectedMessagingTemplates.push(messagingTemplateCache[uuid].formattedMessagingTemplate);
            }
        }
        return newSelectedMessagingTemplates;
    }, [] as Array<FormattedMessagingTemplate>);
};

export const removeUnusedVariablesFromMessagingTemplateLanguageFiles = (
    type: MessagingTemplateTypes,
    template: string,
    languageFiles: Partial<MultiLanguageWrapper<{ [key: string]: any; }>>
) => {
    const variables = getVariablesFromMessagingTemplate(template);

    if (type === MessagingTemplateTypes.DCUI_EMAIL) {
        variables.push({ name: MESSAGING_TEMPLATE_DCUI_EMAIL_SUBJECT_VARIABLE_NAME, value: MESSAGING_TEMPLATE_DCUI_EMAIL_DEFAULT });
    } else if (type === MessagingTemplateTypes.PIN_EMAIL) {
        variables.push({ name: MESSAGING_TEMPLATE_PIN_EMAIL_SUBJECT_VARIABLE_NAME, value: MESSAGING_TEMPLATE_PIN_EMAIL_DEFAULT });
    }

    const pinRequiredIndex = variables.findIndex(variable => variable.value === MESSAGING_TEMPLATE_PIN_REQUIRED_VARIABLE_NAME);
    if (pinRequiredIndex > -1) {
        variables.splice(pinRequiredIndex, 1);
    }

    const dcuiRequiredIndex = variables.findIndex(variable => variable.value === MESSAGING_TEMPLATE_DCUI_REQUIRED_VARIABLE_NAME);
    if (dcuiRequiredIndex > -1) {
        variables.splice(dcuiRequiredIndex, 1);
    }

    const localeToVariablesMap: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};
    //remove any variables that aren't in template (excluding required ones)
    (Object.keys(languageFiles) as Array<LocaleTypes>).forEach(locale => {
        const flattenedLanguageFile = convertJSONObjectToDotSeparatedString(languageFiles[locale]);
        let finalLanguageFile: { [key: string]: any } = {};
        variables.forEach(variable => {
            if (flattenedLanguageFile[variable.name]) {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, flattenedLanguageFile[variable.name]);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            } else {
                const variableJSON = convertDotSeparatedStringToJSONObject(variable.name, variable.value);
                finalLanguageFile = merge({}, finalLanguageFile, variableJSON);
            }
        });
        localeToVariablesMap[locale] = finalLanguageFile;
    });
    return localeToVariablesMap;
};